import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { ContractControlCustomBoxesData, IDocumentTypesDto } from 'app/main/pages/documents/dto/document-listing.dto';
import { IEconomicChart } from '../../contract-control-economics-graph/contract-control-economics.dto';
import { Cost, GetRelatedContract, IDocument, IRevenueDTO } from '../../contract-control.dto';
import { IAddMultisectionForm, IGetContractById, ITotals } from '../../contract-control.service';
import { IPeriodRenewal, IProgressablePeriodRenewal } from '../../store/dto/contract-control-view';
import { ManagerDTO } from '../../store/dto/manager-list.dto';
import { ICostForm, ITotalCost } from '../cc-cost/cost.dto';
import { IRevenueForm, ITotalRevenue } from '../cc-revenue/revenue.dto';
import { CONTRACT_DETAIL_FEATURE_KEY } from './contract-detail.feature';
import { IBox, IField } from 'app/main/pages/settings/documenttype/document-type-settings-modal/document-type-settings.dto';
import { ICustomBoxFormData } from '../cc-custom-box/cc-custom-box.component';
import { IGetCategoriesItemDto } from 'app/main/pages/categories.dto';
import { IOverviewFieldForm } from '../cc-overview/cc-overview.component';
import { IProgressBar } from '../cc-progress-bar/cc-progress-bar.component';
import { Recipient } from 'app/main/pages/documents/dto/document-details.dto';
import { ISectionableRecipient } from 'app/main/pages/edit-template/edit-template.dto';
import { IMetadataItemDto } from 'app/main/pages/settings/documentfields/metadata-item.dto';


const scope = `[${CONTRACT_DETAIL_FEATURE_KEY}]`;

export const ContractDetailActions = createActionGroup({
  source: scope,
  events: {
    /* Fetch Contract */
    'Fetch Contract': props<{ id: number }>(),
    'Fetch Contract Success': props<{ contractControl: IGetContractById; totals: ITotals }>(),
    'Fetch Contract Failure': props<{ error: HttpErrorResponse }>(),

    /* Update Contract Data */
    'Update Contract Data': props<{ column: string; value: string | number }>(),
    'Update Contract Data Success': props<{ contractData: IGetContractById; audit_message: string }>(),
    'Update Contract Data Failure': props<{ error: HttpErrorResponse }>(),

    /* Fetch Layout Settings */
    'Fetch Contract Layout Settings': props<{ document_type_id: number }>(),
    'Fetch Contract Layout Settings Success': props<{ layoutSettings: IDocumentTypesDto }>(),
    'Fetch Contract Layout Settings Failure': props<{ error: HttpErrorResponse }>(),

    /* Cost Actions */
    'Update Cost': props<{ costForm: ICostForm }>(),
    'Update Cost Success': props<{ cost: Cost; totals: ITotalCost; audit_message: string }>(),
    'Update Cost Failure': props<{ error: HttpErrorResponse }>(),

    'Delete Cost': props<{ costId: number }>(),
    'Delete Cost Success': props<{ costId: number; totals: ITotalCost }>(),
    'Delete Cost Failure': props<{ error: HttpErrorResponse }>(),

    /* Revenue Actions */
    'Update Revenue': props<{ revenueForm: IRevenueForm }>(),
    'Update Revenue Success': props<{ revenue: IRevenueDTO; totals: ITotalRevenue; audit_message: string }>(),
    'Update Revenue Failure': props<{ error: HttpErrorResponse }>(),

    'Delete Revenue': props<{ revenueId: number }>(),
    'Delete Revenue Success': props<{ revenueId: number; totals: ITotalRevenue }>(),
    'Delete Revenue Failure': props<{ error: HttpErrorResponse }>(),

    /* Charts */
    'Fetch Economics Charts Data': emptyProps(),
    'Fetch Economics Charts Data Success': props<{ economicsChartData: IEconomicChart[] }>(),
    'Fetch Economics Charts Data Failure': props<{ error: HttpErrorResponse }>(),

    /* Selection & Editing */
    'Select Contract': props<{ contractId: number }>(),
    'Edit Revenue': props<{ revenue: IRevenueDTO }>(),
    'Update Period Renewal': props<{ periodRenewal: IPeriodRenewal | IProgressablePeriodRenewal }>(),
    'Update Period Renewal Progress': props<{ periodRenewal: IPeriodRenewal | IProgressablePeriodRenewal }>(),
    'Update Period Renewal Progress Success': props<{ progress: number }>(),

    /* Utility */
    'Clear Selected Revenue': emptyProps(),
    'Reset Connected Contracts': emptyProps(),
    'Reset State': emptyProps(),

    /* Managers */
    'Fetch Managers': props<{ contract_control_id: number }>(),
    'Fetch Managers Succeeded': props<{ managerList: { managers: ManagerDTO[] } }>(),
    'Fetch Managers Failed': props<{ error: HttpErrorResponse }>(),
    
    'Remove Manager': props<{ manager: ManagerDTO; index: number; contract_control_id: number }>(),
    'Remove Manager Success': props<{ index: number; audit_message: string }>(),
    'Remove Manager Failure': props<{ error: HttpErrorResponse }>(),

    'Link Manager': props<{ formData: IAddMultisectionForm }>(),
    'Link Manager Succeeded': props<{ contract_control_id: number; audit_message: string }>(),
    'Link Manager Failed': props<{ error: HttpErrorResponse }>(),


    /* Related Contracts */
    'Fetch Related Contracts': props<{ contract_control_id: number }>(),
    'Fetch Related Contracts Succeeded': props<{ relatedContracts: GetRelatedContract[] }>(),
    'Fetch Related Contracts Failed': props<{ error: HttpErrorResponse }>(),

    'Link Related Contract': props<{ formData: { contract_control_id: number; related_contract_id: number }, item: GetRelatedContract }>(),
    'Link Related Contract Succeeded': props<{ relatedContract: GetRelatedContract }>(),
    'Link Related Contract Failed': props<{ error: HttpErrorResponse }>(),

    'Unlink Related Contract': props<{ related_contract_id: number; contract_control_id: number }>(),
    'Unlink Related Contract Succeeded': props<{ related_contract_id: number }>(),
    'Unlink Related Contract Failed': props<{ error: HttpErrorResponse }>(),

    /* Contacts */
    'Fetch Contacts': props<{ contract_control_id: number }>(),
    'Fetch Contacts Succeeded': props<{ contacts: ISectionableRecipient[] }>(),
    'Fetch Contacts Failed': props<{ error: HttpErrorResponse }>(),

    'Link Contact': props<{ formData: IAddMultisectionForm }>(),
    'Link Contact Succeeded': props<{ contract_control_id: number; audit_message: string }>(),
    'Link Contact Failed': props<{ error: HttpErrorResponse }>(),

    'Unlink Contact': props<{ sectionable_id: number; contract_control_id: number }>(),
    'Unlink Contact Succeeded': props<{ sectionable_id: number; contract_control_id: number }>(),
    'Unlink Contact Failed': props<{ error: HttpErrorResponse }>(),

    /* Documents */
    'Fetch Documents': props<{ contract_control_id: number }>(),
    'Fetch Documents Succeeded': props<{ documents: IDocument[] }>(),
    'Fetch Documents Failed': props<{ error: HttpErrorResponse }>(),
    
    'Add Documents': props<{ formData: any }>(),
    'Add Documents Succeeded': emptyProps(),
    'Add Documents Failure': props<{ error: HttpErrorResponse }>(),

    'Remove Document': props<{ document: IDocument; index: number; contract_control_id: number }>(),
    'Remove Document Success': props<{ index: number }>(),
    'Remove Document Failure': props<{ error: HttpErrorResponse }>(),

    /* Notes, Todos, Docs, Activities */
    'Get Contract Control Notes': props<{ contract_control_id: number }>(),
    'Get Contract Control Notes Failure': props<{ error: HttpErrorResponse }>(),

    'Get Contract Control Todos': props<{ contract_control_id: number }>(),
    'Get Contract Control Todos Failure': props<{ error: HttpErrorResponse }>(),
    'Update Todo Status': props<{ todoId: number, newStatus: string, previousStatus: string }>(),
    'Set Todo Loading': props<{ todoId: number, isLoading: boolean }>(),

    'Get Contract Control Documents': props<{ contract_control_id: number }>(),
    'Get Contract Control Documents Failure': props<{ error: HttpErrorResponse }>(),

    'Get Contract Control Activities': props<{ contract_control_id: number }>(),
    'Get Contract Control Activities Success': props<{ mergedActivites: any }>(),
    'Get Contract Control Activities Failure': props<{ error: HttpErrorResponse }>(),

    'Fetch Contract Boxes': props<{ contract_control_id: number, document_type_id: number }>(),
    'Fetch Contract Boxes Success': props<{ boxes: IBox[] }>(),
    'Fetch Contract Boxes Failure': props<{ error: HttpErrorResponse }>(),

    'Add Field In Custom Box': props<{ box_id: number, field: IField }>(),

    'Update Custom Box Value' : props<{ formData: ICustomBoxFormData }>(),
    'Update Custom Box Value Success' : props<{ boxData: ICustomBoxFormData, contract_control_custom_boxes_data: ContractControlCustomBoxesData }>(),
    'Update Custom Box Value Failure' : props<{ error: HttpErrorResponse, boxData: ICustomBoxFormData }>(),

    'Get Overview Custom Fields' : props<{ contract_control_id: number }>(),
    'Get Overview Custom Fields Success' : props<{ fields: IField[], selected_category_ids: number[] }>(),
    'Get Overview Custom Fields Failure' : props<{ error: HttpErrorResponse }>(),
    
    'Update Overview Custom Field Value' : props<{ formData: IOverviewFieldForm }>(),
    'Update Overview Custom Field Value Success' : props<{ contract_control_id: number; formData?: IOverviewFieldForm }>(),
    'Update Overview Custom Field Value Failure' : props<{ error: HttpErrorResponse }>(),

    'Get Contract Progress Bar': props<{ contract_control_id: number }>(),
    'Get Contract Progress Bar Success': props<{ progressBarData: IProgressBar }>(),
    'Get Contract Progress Bar Failure': props<{ error: HttpErrorResponse }>(),

    'Fetch Meta Data Fields': emptyProps(),
    'Fetch Meta Data Fields Success': props<{ metaDataItems: IMetadataItemDto[] }>(),
    'Fetch Meta Data Fields Failure': props<{ error: HttpErrorResponse }>(),


    'Update layout settings': props<{ layoutSettings: IDocumentTypesDto, contract_control_id: number }>(),
    'Update layout settings Success': props<{contract_control_id: number}>(),
    'Update layout settings Failure': props<{ error: HttpErrorResponse }>(),
  },
});
